import React, { useEffect, useState } from "react";
import { Box, Container, Paper, Button } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/auth/useAuth";
import { logoNoBackground } from "assets";
import LogoutIcon from "@mui/icons-material/Logout";
import { MainLoading } from "@/components/loading/MainLoading";

const OnboardingLayout: React.FC = () => {
  const { isLoading, currentUser, userInfo, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRouteReady, setIsRouteReady] = useState(false);

  useEffect(() => {
    if (!isLoading && !currentUser) {
      navigate("/login");
      return;
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (isLoading || !userInfo || !currentUser) {
      setIsRouteReady(false);
      return;
    }

    if (
      userInfo.needsInitialPasswordSet &&
      location.pathname !== "/needs-reset-password"
    ) {
      navigate("/needs-reset-password", { replace: true });
      return;
    }

    if (!userInfo.needsInitialPasswordSet && !userInfo.needsOnboarding) {
      navigate(`/${userInfo.role}`, { replace: true });
      return;
    }

    if (
      !userInfo.needsInitialPasswordSet &&
      userInfo.needsOnboarding &&
      location.pathname === "/needs-reset-password"
    ) {
      if (userInfo.role === "admin") {
        navigate(`/freelancer-onboarding`, {
          replace: true,
          state: { fromPasswordReset: true },
        });
      } else {
        navigate(`/${userInfo.role.toLowerCase()}-onboarding`, {
          replace: true,
          state: { fromPasswordReset: true },
        });
      }
      return;
    }

    setIsRouteReady(true);
  }, [isLoading, userInfo, currentUser, location.pathname, navigate]);

  const handleLogout = async () => {
    logout().then(() => {
      navigate("/login");
    });
  };

  if (!isRouteReady || isLoading || !userInfo || !currentUser) {
    return <MainLoading />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.grey[100],
        paddingTop: { xs: "2vh", sm: "5vh" },
        position: "relative",
        pt: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
      }}
    >
      <Button
        onClick={handleLogout}
        startIcon={<LogoutIcon />}
        sx={{
          position: "absolute",
          top: { xs: 8, sm: 16 },
          right: { xs: 8, sm: 16 },
          color: "black",
          fontSize: { xs: "0.875rem", sm: "1rem" },
          py: { xs: 0.5, sm: 1 },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        Sign Out
      </Button>
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={logoNoBackground}
            alt="FreeTech Logo"
            sx={{
              width: { xs: "150px", sm: "200px" },
              height: "auto",
              marginTop: { xs: 2, sm: 4 },
              marginBottom: { xs: 2, sm: 4 },
            }}
          />
          <Paper
            elevation={6}
            sx={{
              padding: { xs: 2, sm: 4 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "white",
              width: "100%",
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <Outlet />
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingLayout;
